import { useConfirm } from "material-ui-confirm";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { IconButton, Tooltip, CircularProgress, Link } from "@material-ui/core";
import ArchiveIcon from "@material-ui/icons/Archive";
import CopyIcon from "@material-ui/icons/FileCopy";
import PauseIcon from "@material-ui/icons/PauseCircleFilled";
import UnpauseIcon from "@material-ui/icons/PlayCircleFilled";
import UnarchiveIcon from "@material-ui/icons/Unarchive";

import { useSubaccount } from "src/components/SubaccountProvider";
import Table, { TableColumns } from "src/components/Table";
import PinIcon from "src/components/icons/PushPin";
import UnpinIcon from "src/components/icons/PushPinOutlined";
import { CampaignStatus } from "src/graphql/generated/globalTypes";
import { listCampaigns_listCampaigns_campaigns } from "src/graphql/generated/listCampaigns";

import { CAMPAIGN_STATUS_LABELS } from "./constants";

function labelForStatus(
  campaign: listCampaigns_listCampaigns_campaigns
): string | null {
  return (
    CAMPAIGN_STATUS_LABELS.find(status => status.value === campaign.status)
      ?.label ?? null
  );
}

type CampaignAction =
  | "archive"
  | "pause"
  | "unpause"
  | "unarchive"
  | "copy"
  | "togglePinned";
const actionsForStatus: { [K in CampaignStatus]: CampaignAction[] } = {
  [CampaignStatus.DRAFT]: ["archive", "copy", "togglePinned"],
  [CampaignStatus.ACTIVE]: ["pause", "copy", "togglePinned"],
  [CampaignStatus.PAUSING]: ["copy", "togglePinned"],
  [CampaignStatus.PAUSED]: ["unpause", "archive", "copy", "togglePinned"],
  [CampaignStatus.ARCHIVED]: ["unarchive", "copy", "togglePinned"],
  [CampaignStatus.PERMANENTLY_ARCHIVED]: ["copy", "togglePinned"],
  [CampaignStatus.COMPLETE]: ["archive", "copy", "togglePinned"],
  [CampaignStatus.STALLED]: ["archive", "copy", "togglePinned"]
};

export default function CampaignTable(
  props: {
    rows: listCampaigns_listCampaigns_campaigns[];
    handleArchive: (campaignId: string) => Promise<void>;
    handlePause: (campaignId: string) => Promise<void>;
    handleUnpause: (campaignId: string) => Promise<void>;
    handleUnarchive: (campaignId: string) => Promise<void>;
    handleCopy: (campaignId: string) => Promise<void>;
    handleSetPinned: (campaignId: string, pinned: boolean) => Promise<void>;
  } & (
    | {
        noFooter?: false;
        page: number;
        handleChangePage: (newPage: number) => void;
        rowsPerPage: number;
        handleChangeRowsPerPage: (newRowsPerPage: number) => void;
        totalCount: number;
      }
    | {
        noFooter: true;
      }
  )
): JSX.Element {
  const {
    rows,
    handleArchive,
    handlePause,
    handleUnpause,
    handleUnarchive,
    handleCopy,
    handleSetPinned,
    noFooter
  } = props;

  const { subaccountLink } = useSubaccount()!;
  const [loadingCampaigns, setLoadingCampaigns] = useState(new Set());
  const confirm = useConfirm();

  function markCampaignLoading(campaignId: string) {
    const newSet = new Set(loadingCampaigns);
    newSet.add(campaignId);
    setLoadingCampaigns(newSet);
  }

  function unmarkCampaignLoading(campaignId: string) {
    const newSet = new Set(loadingCampaigns);
    newSet.delete(campaignId);
    setLoadingCampaigns(newSet);
  }

  function makeHandler(
    campaignId: string,
    callback: (campaignId: string) => Promise<void>,
    {
      confirmTitle,
      confirmMessage
    }: { confirmTitle?: string; confirmMessage?: string } = {}
  ) {
    return async () => {
      if (confirmTitle || confirmMessage) {
        try {
          await confirm({ title: confirmTitle, description: confirmMessage });
        } catch {
          // user cancelled
          return;
        }
      }
      markCampaignLoading(campaignId);
      await callback(campaignId);
      unmarkCampaignLoading(campaignId);
    };
  }

  function renderActions(campaign: listCampaigns_listCampaigns_campaigns) {
    if (loadingCampaigns.has(campaign.id)) {
      return <CircularProgress size={24} />;
    }

    return actionsForStatus[campaign.status].map(
      (action: CampaignAction): JSX.Element | null => {
        switch (action) {
          case "archive":
            return (
              <Tooltip key={action} title="Archive">
                <IconButton
                  onClick={makeHandler(campaign.id, handleArchive, {
                    confirmTitle: "Archive Campaign",
                    confirmMessage: `Are you sure you want to archive the campaign "${campaign.name}"? You will be able to unarchive it for at least 48 hours, but after that we'll release the phone number and you won't be able to unarchive the campaign.`
                  })}
                >
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
            );
          case "unarchive":
            return (
              <Tooltip key={action} title="Unarchive">
                <IconButton
                  onClick={makeHandler(campaign.id, handleUnarchive, {
                    confirmTitle: "Unarchive Campaign",
                    confirmMessage: `Are you sure you want to unarchive the campaign "${campaign.name}"?`
                  })}
                >
                  <UnarchiveIcon />
                </IconButton>
              </Tooltip>
            );
          case "pause":
            return (
              <Tooltip key={action} title="Pause">
                <IconButton
                  onClick={makeHandler(campaign.id, handlePause, {
                    confirmTitle: "Pause Campaign",
                    confirmMessage: `Are you sure you want to pause the campaign "${campaign.name}"? Calls in progress will finish up, then all callers will be notified that the campaign is paused and will be disconnected. You can always resume the campaign later.`
                  })}
                >
                  <PauseIcon />
                </IconButton>
              </Tooltip>
            );
          case "unpause":
            return (
              <Tooltip key={action} title="Resume">
                <IconButton
                  onClick={makeHandler(campaign.id, handleUnpause, {
                    confirmTitle: "Resume Campaign",
                    confirmMessage: `Are you sure you want to resume the campaign "${campaign.name}"?`
                  })}
                >
                  <UnpauseIcon />
                </IconButton>
              </Tooltip>
            );
          case "copy":
            return (
              <Tooltip key={action} title="Copy">
                <IconButton onClick={makeHandler(campaign.id, handleCopy)}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            );
          case "togglePinned":
            return (
              <Tooltip key={action} title={campaign.pinned ? "Unpin" : "Pin"}>
                <IconButton
                  onClick={makeHandler(campaign.id, campaignId =>
                    handleSetPinned(campaignId, !campaign.pinned)
                  )}
                >
                  {campaign.pinned ? <UnpinIcon /> : <PinIcon />}
                </IconButton>
              </Tooltip>
            );
        }
      }
    );
  }

  const columns: TableColumns<listCampaigns_listCampaigns_campaigns> = [
    {
      key: "name",
      title: "Name",
      renderValue: campaign => (
        <Link
          underline="always"
          to={subaccountLink(
            campaign.status === CampaignStatus.DRAFT
              ? `/campaigns/${campaign.id}/basics`
              : `/campaigns/${campaign.id}/start`
          )}
          component={RouterLink}
        >
          {campaign.name}
        </Link>
      )
    },
    {
      key: "createdAt",
      title: "Created At",
      renderValue: campaign =>
        `${new Date(campaign.created_at).toLocaleDateString()} ${new Date(
          campaign.created_at
        ).toLocaleTimeString()}`
    },
    {
      key: "status",
      title: "Status",
      renderValue: campaign => labelForStatus(campaign)
    },
    {
      // TODO: use rowActions
      key: "actions",
      title: "Action",
      renderValue: campaign => renderActions(campaign)
    },
    {
      key: "createdBy",
      title: "Created By",
      renderValue: campaign => (
        <div>
          {campaign.createdByAdmin && (
            <span>{campaign.createdByAdmin.fullName}</span>
          )}
        </div>
      )
    }
  ];

  if (noFooter) {
    return <Table columns={columns} rows={rows} noFooter />;
  } else {
    return (
      <Table
        columns={columns}
        rows={rows}
        totalCount={props.totalCount}
        rowsPerPage={props.rowsPerPage}
        onChangeRowsPerPage={props.handleChangeRowsPerPage}
        page={props.page}
        onChangePage={props.handleChangePage}
      />
    );
  }
}
